import type { RouteRecordRaw } from 'vue-router'
import { P } from '@shared/domain/permissions'
import { placeholder } from '../placeholder'

const workspace: RouteRecordRaw = {
  path: '/ops',
  component: () => import('@/layouts/WorkspaceLayout.vue'),
  meta: { workspace: 'ops', requiresAuth: true },
  children: [
    { path: '', redirect: { name: 'ops.dashboard' } },
    {
      path: 'dashboard',
      name: 'ops.dashboard',
      component: placeholder,
      meta: {
        menu: { labelKey: 'menu.dashboard', icon: 'LayoutDashboard', order: 1 },
        stage: 'All',
      },
    },
    {
      // Banks & Programmes: what LOS published (read-only) plus the programme anchor setup.
      path: '',
      meta: {
        menu: { labelKey: 'menu.ops.programmes', icon: 'Landmark', order: 2 },
        actorTypes: ['PLATFORM_ADMIN'],
        stage: '1',
      },
      children: [
        {
          path: 'banks',
          name: 'ops.banks',
          component: () => import('@ws/ops/references/views/BankList.vue'),
          meta: { menu: { labelKey: 'menu.ops.banks', order: 1 } },
        },
        {
          path: 'banks/:id',
          name: 'ops.banks.detail',
          props: true,
          component: () => import('@ws/ops/references/views/BankDetail.vue'),
          meta: { titleKey: 'references.bank.detailTitle' },
        },
        {
          path: 'products',
          name: 'ops.products',
          component: () => import('@ws/ops/references/views/ProductList.vue'),
          meta: { menu: { labelKey: 'menu.ops.products', order: 2 } },
        },
        {
          path: 'products/:id',
          name: 'ops.products.detail',
          props: true,
          component: () => import('@ws/ops/references/views/ProductDetail.vue'),
          meta: { titleKey: 'references.product.detailTitle' },
        },
        {
          path: 'programmes',
          name: 'ops.programmes',
          component: () => import('@ws/ops/references/views/ProgrammeList.vue'),
          meta: { menu: { labelKey: 'menu.ops.programmeList', order: 3 } },
        },
        {
          path: 'programmes/:id',
          props: true,
          component: () => import('@ws/ops/references/views/ProgrammeLayout.vue'),
          meta: { titleKey: 'references.programme.detailTitle' },
          children: [
            {
              path: '',
              name: 'ops.programmes.detail',
              redirect: (to) => ({ name: 'ops.programmes.overview', params: to.params }),
            },
            {
              path: 'overview',
              name: 'ops.programmes.overview',
              component: () => import('@ws/ops/references/views/ProgrammeOverview.vue'),
              meta: { titleKey: 'references.programme.tabs.overview' },
            },
            {
              path: 'anchor-setup',
              name: 'ops.programmes.anchorSetup',
              component: () => import('@ws/ops/references/views/ProgrammeAnchorSetup.vue'),
              meta: { titleKey: 'references.programme.tabs.anchorSetup' },
            },
            {
              path: 'policies',
              name: 'ops.programmes.policies',
              component: () => import('@ws/ops/references/views/ProgrammePolicies.vue'),
              meta: { titleKey: 'references.programme.tabs.policies' },
            },
            {
              path: 'history',
              name: 'ops.programmes.history',
              component: () => import('@ws/ops/references/views/ProgrammeHistory.vue'),
              meta: { titleKey: 'references.programme.tabs.history' },
            },
          ],
        },
      ],
    },
    {
      // groups without a shared path prefix use path ''
      path: '',
      meta: { menu: { labelKey: 'menu.ops.participants', icon: 'Users', order: 3 } },
      children: [
        {
          path: 'anchors',
          name: 'ops.anchors',
          component: () => import('@ws/ops/anchors/views/AnchorList.vue'),
          meta: { menu: { labelKey: 'menu.ops.anchors' }, stage: '2' },
        },
        {
          path: 'anchors/new',
          name: 'ops.anchors.new',
          component: () => import('@ws/ops/anchors/views/NewAnchor.vue'),
          meta: { permission: P.OPS_ANCHORS_MANAGE, titleKey: 'anchors.new.title', stage: '2' },
        },
        {
          path: 'anchors/:id',
          name: 'ops.anchors.detail',
          props: true,
          component: () => import('@ws/ops/anchors/views/AnchorDetail.vue'),
          meta: { titleKey: 'anchors.detail.title', stage: '2' },
        },
        {
          path: 'anchors/:id/edit',
          name: 'ops.anchors.edit',
          props: true,
          component: () => import('@ws/ops/anchors/views/EditAnchor.vue'),
          meta: { permission: P.OPS_ANCHORS_EDIT, titleKey: 'anchors.edit.title', stage: '2' },
        },
        {
          path: 'suppliers',
          name: 'ops.suppliers',
          component: () => import('@ws/ops/suppliers/views/SupplierList.vue'),
          meta: { menu: { labelKey: 'menu.ops.suppliers' }, stage: '3' },
        },
        {
          path: 'suppliers/new',
          name: 'ops.suppliers.new',
          component: () => import('@ws/ops/suppliers/views/NewSupplier.vue'),
          meta: { permission: P.OPS_SUPPLIERS_CREATE, titleKey: 'suppliers.new.title', stage: '3' },
        },
        {
          path: 'suppliers/:id',
          name: 'ops.suppliers.detail',
          props: true,
          component: () => import('@ws/ops/suppliers/views/SupplierDetail.vue'),
          meta: { titleKey: 'suppliers.detail.title', stage: '3' },
        },
        {
          path: 'suppliers/:id/edit',
          name: 'ops.suppliers.edit',
          props: true,
          component: () => import('@ws/ops/suppliers/views/EditSupplier.vue'),
          meta: { permission: P.OPS_SUPPLIERS_EDIT, titleKey: 'suppliers.edit.title', stage: '3' },
        },
      ],
    },
    {
      path: '',
      meta: { menu: { labelKey: 'menu.invoices', icon: 'FileText', order: 4 } },
      children: [
        {
          path: 'invoice-advices',
          name: 'ops.advices',
          component: () => import('@ws/ops/invoices/views/AdviceList.vue'),
          meta: { menu: { labelKey: 'menu.ops.advices', order: 1 }, stage: '4' },
        },
        {
          path: 'invoice-advices/new',
          name: 'ops.advices.new',
          component: () => import('@ws/ops/invoices/views/NewAdvice.vue'),
          meta: { permission: P.OPS_ADVICES_SUBMIT, titleKey: 'invoices.submit.title', stage: '4' },
        },
        {
          path: 'invoice-advices/:id',
          name: 'ops.advices.detail',
          props: true,
          component: () => import('@ws/ops/invoices/views/AdviceDetail.vue'),
          meta: { titleKey: 'invoices.advices.detailTitleShort', stage: '4' },
        },
        {
          path: 'approved-invoices',
          name: 'ops.approvedInvoices',
          component: () => import('@ws/ops/invoices/views/ApprovedInvoiceList.vue'),
          meta: { menu: { labelKey: 'menu.ops.approvedInvoices', order: 2 }, stage: '4' },
        },
        {
          path: 'approved-invoices/:id',
          name: 'ops.approvedInvoices.detail',
          props: true,
          component: () => import('@ws/ops/invoices/views/ApprovedInvoiceDetail.vue'),
          meta: { titleKey: 'invoices.approved.detailTitleShort', stage: '4' },
        },
        {
          path: 'eligibility-assessments',
          name: 'ops.eligibility',
          component: () => import('@ws/ops/invoices/views/EligibilityList.vue'),
          meta: { menu: { labelKey: 'menu.ops.eligibility', order: 3 }, stage: '4' },
        },
        {
          path: 'eligibility/rules',
          name: 'ops.eligibility.rules',
          component: placeholder,
          meta: {
            permission: P.OPS_ELIGIBILITY_MANAGE,
            menu: { labelKey: 'menu.ops.eligibilityRules', order: 4 },
            stage: '4',
          },
        },
        {
          path: 'eligibility/reason-codes',
          name: 'ops.eligibility.reasonCodes',
          component: placeholder,
          meta: {
            permission: P.OPS_ELIGIBILITY_MANAGE,
            menu: { labelKey: 'menu.ops.reasonCodes', order: 5 },
            stage: '4',
          },
        },
      ],
    },
    {
      path: 'financing',
      name: 'ops.financing',
      component: placeholder,
      meta: { menu: { labelKey: 'menu.ops.financing', icon: 'Banknote', order: 5 }, stage: '5–7' },
    },
    {
      path: 'collections',
      name: 'ops.collections',
      component: placeholder,
      meta: {
        menu: { labelKey: 'menu.ops.collections', icon: 'CalendarClock', order: 6 },
        stage: '8–9',
      },
    },
    {
      path: 'integration',
      meta: { menu: { labelKey: 'menu.integration', icon: 'Plug', order: 7 } },
      children: [
        { path: '', redirect: { name: 'ops.integration.messages' } },
        {
          path: 'messages',
          name: 'ops.integration.messages',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.ops.messageMonitor' }, stage: 'All' },
        },
        {
          path: 'dlq',
          name: 'ops.integration.dlq',
          component: placeholder,
          meta: {
            permission: P.OPS_MESSAGES_REPLAY,
            menu: { labelKey: 'menu.ops.dlq' },
            stage: 'All',
          },
        },
        {
          path: 'idempotency',
          name: 'ops.integration.idempotency',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.ops.idempotency' }, stage: '4' },
        },
      ],
    },
    {
      path: 'admin',
      meta: {
        permission: P.OPS_ADMIN,
        menu: { labelKey: 'menu.ops.admin', icon: 'Settings', order: 8 },
      },
      children: [
        { path: '', redirect: { name: 'ops.admin.users' } },
        {
          path: 'users',
          name: 'ops.admin.users',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.ops.platformUsers' } },
        },
        {
          path: 'notifications',
          name: 'ops.admin.notifications',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.ops.notificationTemplates' } },
        },
        {
          path: 'audit',
          name: 'ops.admin.audit',
          component: placeholder,
          meta: { menu: { labelKey: 'menu.ops.auditLog' } },
        },
      ],
    },
  ],
}

export const opsRoutes: RouteRecordRaw[] = [workspace]
